import * as React from 'react'
import { useField } from 'formik'
import { useSelector } from 'react-redux'
import FormHelperText from '@mui/material/FormHelperText'
import { ENTITY_TYPES } from '@sales-center/common'
import { selectCurrentUser } from '@user/store/authorization'
import { useGetOwnerList } from '@user/store/salesTeam/hooks/useGetOwnerList'
import type { OwnerOption } from './ActivityFormFields.types'

type Props = {
  salesTeamId: number
  owners?: OwnerOption[]
  entityType?: ENTITY_TYPES
}

const UNASSIGNED = 'unassigned'

const OWNER_HINT_ID = 'activity-form-owner-hint'

/**
 * SH-2133: tells the user an event handed to someone else will not show up on
 * their own calendar — the SC feed is owner-scoped, so it genuinely disappears
 * from their view, and saying so at the point of choosing is cheaper than the
 * "where did my event go" support ticket.
 *
 * Deliberately a sibling of `ActivityOwnerField` rather than part of it. This
 * reads the store, and `ActivityOwnerField` is shared with the entity-scoped
 * Activity panel, whose hosts render without a redux Provider; folding the
 * store read into the shared component imposes that dependency on all of them.
 * It is also only wanted on the New Event modal, which the AC names as the two
 * surfaces in scope.
 */
export function OwnerCalendarScopeHint({
  salesTeamId,
  owners,
  entityType,
}: Props) {
  const [{ value: selectedOwnerId }] = useField<string | undefined>('ownerId')
  const currentUser = useSelector(selectCurrentUser)

  // Resolve a display name from whatever list the host already has, falling
  // back to the source OwnerSelect itself renders from, so the hint names the
  // person rather than an id.
  const fetchedOwners = useGetOwnerList(
    entityType ?? ENTITY_TYPES.Lead,
    salesTeamId,
  )
  const selectedOwnerName = React.useMemo(() => {
    if (!selectedOwnerId || selectedOwnerId === UNASSIGNED) return null
    const fromProp = owners?.find(
      (o) => (o.userId ?? o.id) === selectedOwnerId,
    )?.name
    return (
      fromProp ??
      fetchedOwners.find((o) => o.id === selectedOwnerId)?.name ??
      null
    )
  }, [owners, fetchedOwners, selectedOwnerId])

  // Until the current user is known there is nothing to compare against, so
  // stay silent rather than warn about the user's own event. GUIDs arrive
  // with inconsistent casing, so compare case-insensitively.
  const myId = currentUser?.userId
  const notMine =
    !!myId &&
    !!selectedOwnerId &&
    selectedOwnerId.toLowerCase() !== myId.toLowerCase()
  if (!notMine) return null

  const text =
    selectedOwnerId === UNASSIGNED
      ? 'This event will not be assigned to anyone and will not appear on your calendar.'
      : selectedOwnerName
        ? `This event will be assigned to ${selectedOwnerName} and will not appear on your calendar.`
        : // Better silent than "assigned to undefined" while the list loads.
          null

  if (!text) return null

  return (
    <FormHelperText
      id={OWNER_HINT_ID}
      data-test-id="activity-form-owner-hint"
      // Appears only after the Owner selection changes, so without a live
      // region a screen-reader user completes the choice never hearing it.
      role="status"
      aria-live="polite"
    >
      {text}
    </FormHelperText>
  )
}
